/**
 * Меню комментария: размер шрифта, цвет и удаление. Открывается правым кликом по
 * выноске или кнопкой ▾ на её рамке и стоит справа от неё, поверх холста, — текст
 * комментария при этом виден; у правого края холста меню уходит влево.
 * Клик мимо меню или Esc закрывают его.
 */
import { useEffect, useRef, useState } from "react";
import { UIKit } from "@/shared/ui";
import { COMMENT_COLORS, COMMENT_FONT_SIZE_RANGE } from "@/shared/store/layers";
import type { PlanComment } from "@/shared/plan";
import { commentBox, planToCanvasPx } from "../logic";

interface Props {
	comment: PlanComment;
	/** Видимая часть плана, мм: угол и ширина — чтобы меню не вылезло за правый край */
	viewBox: { x: number; y: number; w: number };
	mmPerPx: number;
	onFontSize: (size: number) => void;
	onColor: (color: string) => void;
	onEdit: () => void;
	onDelete: () => void;
	onClose: () => void;
}

const MENU_WIDTH = 212;

export const CommentMenu = ({
	comment,
	viewBox,
	mmPerPx,
	onFontSize,
	onColor,
	onEdit,
	onDelete,
	onClose,
}: Props) => {
	const ref = useRef<HTMLDivElement>(null);
	const box = commentBox(comment, mmPerPx);
	// Справа от кнопки ▾ в правом нижнем углу рамки; не влезает — слева от рамки
	const at = planToCanvasPx({ x: box.x + box.width, y: box.y + box.height }, viewBox, mmPerPx);
	// Кегль в поле — строкой: пока вписывают «18», в поле бывает и пусто, и «1»
	const [fontInput, setFontInput] = useState(String(comment.fontSize));
	const { min, max } = COMMENT_FONT_SIZE_RANGE;
	const applyFont = (raw: string) => {
		setFontInput(raw);
		const size = Number(raw);
		if (Number.isFinite(size) && size >= min && size <= max) onFontSize(Math.round(size));
	};
	const canvasWidth = viewBox.w / mmPerPx;
	const left =
		at.x + 14 + MENU_WIDTH <= canvasWidth ? at.x + 14 : at.x - box.width / mmPerPx - MENU_WIDTH - 8;

	useEffect(() => {
		const onPointerDown = (e: PointerEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) onClose();
		};
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("pointerdown", onPointerDown, true);
		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("pointerdown", onPointerDown, true);
			window.removeEventListener("keydown", onKeyDown);
		};
	}, [onClose]);

	return (
		<div
			ref={ref}
			role="menu"
			className="absolute z-20 flex flex-col gap-2 rounded-xl border border-border bg-surface-raised p-2 shadow-xl"
			style={{ left, top: at.y - 8, width: MENU_WIDTH }}
		>
			<button
				type="button"
				role="menuitem"
				className="rounded-md px-2 py-1.5 text-left text-xs text-ink hover:bg-slate-100"
				onClick={onEdit}
			>
				Изменить текст
			</button>

			<div className="flex flex-col gap-1 px-1">
				<UIKit.Text as="span" className="text-xs text-ink">
					Шрифт
				</UIKit.Text>
				<label className="flex items-center gap-2">
					<UIKit.NumberInput
						bare
						type="number"
						inputMode="numeric"
						ariaLabel="Размер шрифта"
						min={min}
						max={max}
						inputClassName="w-16 rounded-md border border-border px-2 py-1 text-xs text-ink tabular-nums focus:border-accent focus:outline-none"
						value={fontInput}
						onChange={applyFont}
						// Ушли из поля с неподходящим числом — показываем действующий кегль
						onBlur={() => setFontInput(String(comment.fontSize))}
						onKeyDown={(e) => {
							if (e.key === "Enter") e.currentTarget.blur();
						}}
					/>
					<UIKit.Text as="span" className="text-xs text-ink">
						от {min} до {max}
					</UIKit.Text>
				</label>
			</div>

			<div className="flex flex-col gap-1 px-1">
				<UIKit.Text as="span" className="text-xs text-ink">
					Цвет
				</UIKit.Text>
				<div className="flex gap-1.5">
					{COMMENT_COLORS.map((color) => (
						<button
							key={color}
							type="button"
							aria-label={`Цвет ${color}`}
							className={`size-6 rounded-full border-2${
								comment.color === color
									? " border-accent"
									: " border-surface-raised ring-1 ring-border"
							}`}
							style={{ background: color }}
							onClick={() => onColor(color)}
						/>
					))}
				</div>
			</div>

			<button
				type="button"
				role="menuitem"
				className="mt-0.5 flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-danger hover:bg-red-50"
				onClick={onDelete}
			>
				<UIKit.IconTrash />
				Удалить комментарий
			</button>
		</div>
	);
};
